<template>
  <KTCodePreview v-bind:title="'Bù trừ kiểm kho'">
    <template v-slot:preview>
      <div v-if="onLoading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">Đang tải dữ liệu...</p>
      </div>
      <div v-else>
        <!-- Sheet info -->
        <b-row class="mb-4">
          <b-col md="4">
            <b-list-group flush>
              <b-list-group-item class="border-0 px-0 py-1">
                <span class="font-weight-bold text-muted mr-2">Mã phiếu:</span>
                <span class="font-weight-bold text-primary">{{ sheetInfo.code }}</span>
              </b-list-group-item>
              <b-list-group-item class="border-0 px-0 py-1">
                <span class="font-weight-bold text-muted mr-2">Cửa hàng:</span>
                <span>{{ sheetInfo.storeName }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>

        <!-- Diff summary -->
        <b-alert
          v-if="!hasProducts && !onLoading"
          variant="success"
          show
        >
          <i class="fas fa-check-circle mr-2"></i>
          Phiếu kiểm kê không có sản phẩm lệch tồn.
        </b-alert>

        <!-- Product diff table -->
        <b-table
          v-if="hasProducts"
          :items="pagedProducts"
          :fields="fields"
          outlined
          bordered
          head-row-variant="secondary"
          :busy="onLoading"
          responsive
        >
          <template v-slot:cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template v-slot:cell(barCode)="data">
            <span class="font-weight-bold">{{ data.item.barCode || '—' }}</span>
          </template>

          <template v-slot:cell(inStockQuantity)="data">
            {{ data.item.inStockQuantity }}
          </template>

          <template v-slot:cell(diff)="data">
            <template v-if="data.item.productType === PRODUCT_TYPE.PRODUCT_IMEI">
              <span
                v-if="data.item.imeiMissings.length"
                class="text-danger font-weight-bold d-block"
              >
                -{{ data.item.imeiMissings.length }}
              </span>
              <span
                v-if="data.item.imeiExtras.length"
                class="text-success font-weight-bold d-block"
              >
                +{{ data.item.imeiExtras.length }}
              </span>
            </template>
            <template v-else>
              <span
                :class="data.item.subtractQuantity > 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'"
              >
                {{ data.item.subtractQuantity > 0 ? '+' : '' }}{{ data.item.subtractQuantity }}
              </span>
            </template>
          </template>

          <template v-slot:cell(action)="data">
            <div v-if="data.item.productType === PRODUCT_TYPE.PRODUCT_IMEI">
              <div v-if="data.item.imeiMissings.length" class="mb-1">
                <b-badge variant="danger" class="mr-1">Xuất kho {{ data.item.imeiMissings.length }}</b-badge>
                <small class="text-muted">IMEI: {{ data.item.imeiMissings.join(', ') }}</small>
              </div>
              <div v-if="data.item.imeiExtras.length">
                <b-badge variant="success" class="mr-1">Nhập kho {{ data.item.imeiExtras.length }}</b-badge>
                <small class="text-muted">IMEI: {{ data.item.imeiExtras.join(', ') }}</small>
              </div>
            </div>
            <div v-else>
              <b-badge :variant="data.item.subtractQuantity < 0 ? 'danger' : 'success'">
                {{ data.item.subtractQuantity > 0 ? 'Nhập kho' : 'Xuất kho' }}
                {{ Math.abs(data.item.subtractQuantity) }} sản phẩm
              </b-badge>
            </div>
          </template>
        </b-table>

        <b-row>
          <b-col>
            <p v-if="hasProducts" class="mt-3 text-dark" style="font-weight: 500">
              Tổng số: {{ products.length }}
            </p>
          </b-col>
          <b-pagination
            class="custom-pagination"
            v-show="products.length > perPage"
            v-model="currentPage"
            :total-rows="products.length"
            :per-page="perPage"
            align="right"
          />
        </b-row>
      </div>
    </template>

    <template v-slot:foot>
      <b-button
        size="sm"
        variant="primary"
        class="font-weight-bolder mr-4"
        :disabled="onLoading || onSubmitting || !hasProducts"
        @click="confirmCreateBalance"
      >
        <b-spinner v-if="onSubmitting" small class="mr-1" />
        <i v-else class="fas fa-exchange-alt mr-1"></i>
        Tạo phiếu xuất nhập kho
      </b-button>
      <b-button
        size="sm"
        variant="secondary"
        class="font-weight-bolder"
        @click="$router.push({ name: 'list-inventory-check' })"
      >
        Hủy
      </b-button>
    </template>
  </KTCodePreview>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastSuccess, handleError } from '@/utils/common';
import { showConfirmDialog } from '@/utils/sweet-alert2';

const PRODUCT_TYPE = {
  PRODUCT: 1,
  PRODUCT_IMEI: 2,
};

export default {
  components: { KTCodePreview },
  data() {
    return {
      onLoading: false,
      onSubmitting: false,
      currentPage: 1,
      perPage: 10,
      sheetInfo: {
        id: null,
        code: '',
        storeName: '',
        status: null,
      },
      products: [],
      PRODUCT_TYPE,
      fields: [
        {
          key: 'index',
          label: 'STT',
          thStyle: { width: '5%' },
          thClass: 'text-center text-dark',
          tdClass: 'text-center align-middle',
        },
        {
          key: 'barCode',
          label: 'Mã SP',
          thStyle: { width: '12%' },
          thClass: 'text-center text-dark',
          tdClass: 'text-left align-middle',
        },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          thClass: 'text-center text-dark',
          tdClass: 'text-left align-middle',
        },
        {
          key: 'inStockQuantity',
          label: 'Tồn',
          thStyle: { width: '8%' },
          thClass: 'text-center text-dark',
          tdClass: 'text-center align-middle',
        },
        {
          key: 'diff',
          label: 'Thừa thiếu',
          thStyle: { width: '10%' },
          thClass: 'text-center text-dark',
          tdClass: 'text-center align-middle',
        },
        {
          key: 'action',
          label: 'Chuyển',
          thStyle: { width: '30%' },
          thClass: 'text-center text-dark',
          tdClass: 'text-left align-middle',
        },
      ],
    };
  },
  computed: {
    hasProducts() {
      return this.products.length > 0;
    },
    pagedProducts() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.products.slice(start, start + this.perPage);
    },
  },
  methods: {
    async fetchBalancePreview() {
      const id = this.$route.query.id;
      if (!id) {
        this.$router.push({ name: 'list-inventory-check' });
        return;
      }
      this.onLoading = true;
      try {
        const { data } = await ApiService.get(`v1/stock-check-sheet/${id}/balance-preview`);
        const result = data.data;
        this.sheetInfo = {
          id: result.id,
          code: result.code,
          storeName: result.storeName,
          status: result.status,
        };
        this.products = result.products || [];
      } catch (err) {
        handleError(err);
        this.$router.push({ name: 'list-inventory-check' });
      } finally {
        this.onLoading = false;
      }
    },
    async confirmCreateBalance() {
      showConfirmDialog(
        'Tạo phiếu xuất nhập kho?',
        `Hệ thống sẽ tạo phiếu bù trừ tồn kho cho phiếu kiểm kê "${this.sheetInfo.code}". Bạn có chắc muốn tiếp tục?`,
      )
        .then(() => this.createBalance())
        .catch(() => {});
    },
    async createBalance() {
      this.onSubmitting = true;
      try {
        const { data } = await ApiService.post(
          `v1/stock-check-sheet/${this.sheetInfo.id}/balance`,
          {},
        );
        makeToastSuccess(data.message || 'Tạo phiếu bù trừ thành công!');
        this.$router.push({ name: 'list-inventory-check' });
      } catch (err) {
        handleError(err);
      } finally {
        this.onSubmitting = false;
      }
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kiểm tra tồn kho', route: { name: 'list-inventory-check' } },
      { title: 'Bù trừ kiểm kho' },
    ]);
    this.fetchBalancePreview();
  },
};
</script>
